import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Collapse,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  CardGiftcard as GiftIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";

import {
  getGiveaways,
  startGiveaway,
} from "../../features/giveaways/giveawaysSlice";
import {
  openWinnerDialog,
  closeWinnerDialog,
} from "../../store/slices/notificationsSlice";
import WinnerNotificationDialog from "../../components/admin/WinnerNotificationDialog";
import GiveawayDetailDialog from "../../components/admin/GiveawayDetailDialog";

const GiveawaysList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { giveaways, totalGiveaways, loading, error } = useSelector(
    (state) => state.giveaways
  );
  const { winnerDialogOpen, selectedWinner, selectedGiveaway } = useSelector(
    (state) => state.notifications
  );

  // Local state for pagination and filtering
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "all", // 'all', 'active', 'upcoming', 'completed'
    sortBy: "newest", // 'newest', 'oldest', 'name_asc', 'name_desc', 'end_date'
    dateRange: "all", // 'all', 'this_week', 'this_month', 'last_month', 'custom'
  });
  const [starting, setStarting] = useState(false);

  // Dialog state
  const [detailDialog, setDetailDialog] = useState({
    open: false,
    giveaway: null,
  });

  // Separate state for the actual search query used in API calls
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    // Fetch giveaways with pagination and filters
    dispatch(
      getGiveaways({
        page: page + 1, // API uses 1-indexed pages
        perPage: rowsPerPage,
        search: searchQuery,
        status: filters.status !== "all" ? filters.status : undefined,
        sortBy: filters.sortBy,
        dateRange: filters.dateRange !== "all" ? filters.dateRange : undefined,
      })
    );
  }, [dispatch, page, rowsPerPage, searchQuery, filters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Update search query to trigger API call
    setSearchQuery(searchTerm);
    // Reset to first page when searching
    setPage(0);
  };

  const handleRefresh = () => {
    dispatch(
      getGiveaways({
        page: page + 1,
        perPage: rowsPerPage,
        search: searchQuery,
        status: filters.status !== "all" ? filters.status : undefined,
        sortBy: filters.sortBy,
        dateRange: filters.dateRange !== "all" ? filters.dateRange : undefined,
      })
    );
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
    setPage(0); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      status: "all",
      sortBy: "newest",
      dateRange: "all",
    });
    setSearchTerm("");
    setPage(0);
  };

  const hasActiveFilters = () => {
    return (
      filters.status !== "all" ||
      filters.sortBy !== "newest" ||
      filters.dateRange !== "all" ||
      searchTerm !== ""
    );
  };

  const handleStartGiveaway = async () => {
    setStarting(true);
    try {
      const result = await dispatch(startGiveaway()).unwrap();
      console.log("Giveaway started successfully:", result);
      // If a winner was drawn, open the winner notification dialog
      if (result.winner && result.giveaway) {
        dispatch(
          openWinnerDialog({
            winner: result.winner,
            giveaway: result.giveaway,
          })
        );
      }
    } catch (error) {
      console.error("Failed to start giveaway:", error);
    } finally {
      setStarting(false);
    }
  };

  // Helper function to determine status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "upcoming":
        return "info";
      case "completed":
        return "default";
      default:
        return "default";
    }
  };

  // Use real data from Redux store with proper null checking
  const displayGiveaways = Array.isArray(giveaways)
    ? giveaways
    : giveaways?.data;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Giveaways</Typography>
        <Button
          variant="contained"
          startIcon={
            starting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <GiftIcon />
            )
          }
          onClick={handleStartGiveaway}
          disabled={starting}
          sx={{
            background: "linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)",
            boxShadow: "0 3px 5px 2px rgba(255, 105, 135, .3)",
            "&:hover": {
              background: "linear-gradient(45deg, #FE6B8B 60%, #FF8E53 100%)",
            },
          }}
        >
          {starting ? "Starting..." : "Start Giveaway"}
        </Button>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <TextField
              size="small"
              placeholder="Search giveaways..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mr: 2, width: 300 }}
            />
            <Button
              variant="contained"
              type="submit"
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
          </Box>

          <Box>
            {/* <Button
              variant={hasActiveFilters() ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ mr: 1 }}
              color={hasActiveFilters() ? "primary" : "inherit"}
            >
              Filters {hasActiveFilters() && "(Active)"}
            </Button> */}
            {hasActiveFilters() && (
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                sx={{ mr: 1 }}
              >
                Clear Filters
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Advanced Filters Panel */}
        <Collapse in={showFilters}>
          <Divider sx={{ mb: 3 }} />
          <Card variant="outlined" sx={{ bgcolor: "grey.50" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Advanced Filters
              </Typography>

              <Grid container spacing={3}>
                {/* Status Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      label="Status"
                      onChange={(e) =>
                        handleFilterChange("status", e.target.value)
                      }
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="upcoming">Upcoming</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Sort By Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={filters.sortBy}
                      label="Sort By"
                      onChange={(e) =>
                        handleFilterChange("sortBy", e.target.value)
                      }
                    >
                      <MenuItem value="newest">Newest First</MenuItem>
                      <MenuItem value="oldest">Oldest First</MenuItem>
                      <MenuItem value="name_asc">Name A-Z</MenuItem>
                      <MenuItem value="name_desc">Name Z-A</MenuItem>
                      <MenuItem value="end_date_asc">
                        End Date (Earliest)
                      </MenuItem>
                      <MenuItem value="end_date_desc">
                        End Date (Latest)
                      </MenuItem>
                      <MenuItem value="participants_desc">
                        Most Participants
                      </MenuItem>
                      <MenuItem value="participants_asc">
                        Least Participants
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Date Range Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Date Range</InputLabel>
                    <Select
                      value={filters.dateRange}
                      label="Date Range"
                      onChange={(e) =>
                        handleFilterChange("dateRange", e.target.value)
                      }
                    >
                      <MenuItem value="all">All Time</MenuItem>
                      <MenuItem value="this_week">This Week</MenuItem>
                      <MenuItem value="this_month">This Month</MenuItem>
                      <MenuItem value="last_month">Last Month</MenuItem>
                      <MenuItem value="last_3_months">Last 3 Months</MenuItem>
                      <MenuItem value="this_year">This Year</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Filter Actions */}
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters()}
                >
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setShowFilters(false)}
                >
                  Apply Filters
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Collapse>
      </Paper>

      {/* Giveaways Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Winner Name</TableCell>
                <TableCell>Winner Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={40} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : displayGiveaways?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box sx={{ py: 4, textAlign: "center" }}>
                      <GiftIcon
                        sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                      />
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        No giveaways yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Start your first giveaway to see it here!
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                displayGiveaways?.map((giveaway) => (
                  <TableRow key={giveaway.id}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <GiftIcon sx={{ mr: 1, color: "primary.main" }} />
                        <Typography variant="body1">
                          {giveaway.title}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="body1">
                          {giveaway.winner?.first_name}{" "}
                          {giveaway.winner?.last_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="body1">
                          {giveaway.winner?.user_email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={giveaway.status
                          ?.split("_")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .join(" ")}
                        color={getStatusColor(giveaway?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(
                        giveaway.draw_date || giveaway.draw_date
                      ).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          onClick={() => {
                            setDetailDialog({
                              open: true,
                              giveaway: giveaway,
                            });
                          }}
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalGiveaways || displayGiveaways?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Winner Notification Dialog */}
      <WinnerNotificationDialog
        open={winnerDialogOpen}
        onClose={() => dispatch(closeWinnerDialog())}
        winner={selectedWinner}
        giveaway={selectedGiveaway}
      />

      {/* Giveaway Detail Dialog */}
      <GiveawayDetailDialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, giveaway: null })}
        giveaway={detailDialog.giveaway}
      />
    </Box>
  );
};

export default GiveawaysList;
